'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface LicensingAgreementPanelProps {
  agreedNoResale: boolean;
  agreedModifications: boolean;
  onAgreedNoResaleChange: (v: boolean) => void;
  onAgreedModificationsChange: (v: boolean) => void;
}

interface Section {
  id: string;
  icon: string;
  title: string;
  summary: string;
  details: React.ReactNode;
}

export default function LicensingAgreementPanel({
  agreedNoResale,
  agreedModifications,
  onAgreedNoResaleChange,
  onAgreedModificationsChange,
}: LicensingAgreementPanelProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const sections: Section[] = [
    {
      id: 'quantity',
      icon: 'KeyIcon',
      title: 'License Quantity',
      summary:
        'Choose how many activation keys you need for this template.',
      details: (
        <ul className="space-y-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <li className="flex items-start gap-2"><span className="mt-0.5 text-accent">•</span>Each license generates a unique activation key.</li>
          <li className="flex items-start gap-2"><span className="mt-0.5 text-accent">•</span>Keys are one-time use and tied to your account.</li>
          <li className="flex items-start gap-2"><span className="mt-0.5 text-accent">•</span>You may purchase multiple licenses for team or client use.</li>
        </ul>
      ),
    },
    {
      id: 'agreement',
      icon: 'DocumentTextIcon',
      title: 'License Agreement',
      summary:
        'ZyForge templates are licensed for your use — not sold. You may not resell or redistribute them.',
      details: (
        <div className="space-y-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <p>Templates remain the sole property of ZyForge. Your purchase grants a <strong>limited, non-exclusive license</strong>.</p>
          <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>You may NOT:</p>
          <ul className="space-y-1">
            {[
              'Resell the template',
              'Redistribute the template',
              'Upload it to any marketplace',
              'Share activation keys',
              'Claim the template as your own creation',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <Icon name="XMarkIcon" size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' } as React.CSSProperties} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: 'modifications',
      icon: 'WrenchScrewdriverIcon',
      title: 'Modifications Allowed',
      summary:
        'You can customize and modify the template, but ownership does not transfer.',
      details: (
        <ul className="space-y-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <li className="flex items-start gap-2"><span className="mt-0.5" style={{ color: '#22C55E' }}>✓</span>You may edit code, add components, change styling, etc.</li>
          <li className="flex items-start gap-2"><Icon name="XMarkIcon" size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' } as React.CSSProperties} />Modifications do NOT grant ownership of the original template.</li>
          <li className="flex items-start gap-2"><Icon name="XMarkIcon" size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' } as React.CSSProperties} />Modified versions remain bound by the ZyForge license.</li>
          <li className="flex items-start gap-2"><Icon name="XMarkIcon" size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' } as React.CSSProperties} />Anti-tamper and activation protections must remain intact.</li>
        </ul>
      ),
    },
    {
      id: 'legal',
      icon: 'ShieldCheckIcon',
      title: 'Legal Protections',
      summary:
        'Your template includes built-in copyright and licensing protections.',
      details: (
        <div className="space-y-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
          {[
            {
              label: 'Copyright Protection',
              text: 'All original code, images, and creative content are automatically protected.',
            },
            {
              label: 'DMCA Takedown',
              text: 'If someone copies your template, you may file a DMCA notice with their hosting provider.',
            },
            {
              label: 'License Agreements',
              text: 'Purchasing a template grants a limited license — not ownership.',
            },
            {
              label: 'Trade Dress / Look & Feel',
              text: 'Distinctive visual identity may be protected under trademark/trade dress law.',
            },
            {
              label: 'Limitations',
              text: 'Generic layouts or functional elements may not be protected.',
            },
          ].map((item) => (
            <div key={item.label}>
              <p className="font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'gift',
      icon: 'GiftIcon',
      title: 'Gift This Template',
      summary:
        'Send this template to someone else using their email address.',
      details: (
        <ul className="space-y-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <li className="flex items-start gap-2"><span className="mt-0.5 text-accent">•</span>The recipient must create an account using the same email you entered.</li>
          <li className="flex items-start gap-2"><span className="mt-0.5 text-accent">•</span>Once matched, the template is added to their account.</li>
          <li className="flex items-start gap-2"><span className="mt-0.5 text-accent">•</span>The activation key becomes claimable by the recipient only.</li>
          <li className="flex items-start gap-2"><Icon name="XMarkIcon" size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' } as React.CSSProperties} />You will not have access to gifted keys.</li>
        </ul>
      ),
    },
  ];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      {/* Header */}
      <div
        className="px-5 py-4"
        style={{ background: 'var(--accent-light)', borderBottom: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Icon name="DocumentCheckIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
          <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
            Licensing &amp; Ownership Agreement
          </h3>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Please review the following terms before placing your order.
        </p>
      </div>

      {/* Sections */}
      <div style={{ background: 'var(--bg-card)' }}>
        {sections.map((section, idx) => (
          <div
            key={section.id}
            style={{
              borderBottom:
                idx < sections.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            {/* Summary row */}
            <div className="px-5 py-3.5">
              <div className="flex items-start gap-3">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'var(--accent-light)' }}
                >
                  <Icon
                    name={section.icon as 'KeyIcon'}
                    size={14}
                    style={{ color: 'var(--accent)' } as React.CSSProperties}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                    {section.title}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {section.summary}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(section.id)}
                  className="flex items-center gap-1 text-xs flex-shrink-0 mt-0.5 transition-colors"
                  style={{ color: 'var(--accent)' }}
                >
                  {expanded[section.id] ? 'Less' : 'More'}
                  <Icon
                    name={expanded[section.id] ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={12}
                  />
                </button>
              </div>

              {/* Expandable details */}
              {expanded[section.id] && (
                <div
                  className="mt-3 ml-10 p-3 rounded-xl"
                  style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
                >
                  {section.details}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Required Checkboxes */}
      <div
        className="px-5 py-4 space-y-3"
        style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}
      >
        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
          Required Agreements
        </p>

        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={agreedNoResale}
              onChange={(e) => onAgreedNoResaleChange(e.target.checked)}
              className="sr-only"
            />
            <div
              className="w-5 h-5 rounded flex items-center justify-center transition-all"
              style={{
                background: agreedNoResale ? 'var(--accent)' : 'var(--bg-card)',
                border: `2px solid ${agreedNoResale ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >
              {agreedNoResale && (
                <Icon name="CheckIcon" size={11} style={{ color: '#fff' } as React.CSSProperties} />
              )}
            </div>
          </div>
          <span className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            <em>
              I understand that ZyForge templates are licensed, not sold, and I may not resell or
              redistribute them.
            </em>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={agreedModifications}
              onChange={(e) => onAgreedModificationsChange(e.target.checked)}
              className="sr-only"
            />
            <div
              className="w-5 h-5 rounded flex items-center justify-center transition-all"
              style={{
                background: agreedModifications ? 'var(--accent)' : 'var(--bg-card)',
                border: `2px solid ${agreedModifications ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >
              {agreedModifications && (
                <Icon name="CheckIcon" size={11} style={{ color: '#fff' } as React.CSSProperties} />
              )}
            </div>
          </div>
          <span className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            <em>
              I understand I may modify the template, but the original template remains protected
              and cannot be resold.
            </em>
          </span>
        </label>

        {(!agreedNoResale || !agreedModifications) && (
          <p className="text-xs flex items-center gap-1.5" style={{ color: '#F59E0B' }}>
            <Icon name="ExclamationTriangleIcon" size={13} />
            Both agreements must be accepted to place your order.
          </p>
        )}
      </div>
    </div>
  );
}
